import React, { useEffect, useState } from "react";
import { CustomCheckbox } from "~/components/ui/check_Box";
import styles from "../../index.module.scss";

interface AppOption {
  id: string;
  name: string;
  code: string;
}

interface FieldOption {
  id: string;
  name: string;
  type?: string; // フィールドタイプ情報を追加
}

interface ReferenceTableProps {
  options: {
    noLabel?: boolean;
    referenceTable?: {
      relatedApp: {
        app: string;
        code: string;
      };
      condition: {
        field: string;
        relatedField: string;
      };
      displayFields: string[];
      sort: string;
      size: string;
    };
    [key: string]: any;
  };
  onChange: (key: string, value: any) => void;
  getFieldsForApp: (appId: string) => any[];
  isReadOnly?: boolean;
  appList?: { app_id: string; app_name: string }[]; // アプリ一覧を外部から受け取る
}

const allowedReferenceFieldTypesMap: Record<string, string[]> = {
  // このアプリのフィールドタイプ: [許可される参照先アプリのフィールドタイプ]
  SINGLE_LINE_TEXT: ["SINGLE_LINE_TEXT", "LINK", "LOOKUP"],
  LOOKUP: ["SINGLE_LINE_TEXT", "LINK", "LOOKUP", "NUMBER", /*'CALC',*/ "RECORD_NUMBER"], // ルックアップは複数タイプと紐づく可能性あり
  NUMBER: ["NUMBER", /*'CALC',*/ "RECORD_NUMBER", "LOOKUP"],
  // 'CALC': ['NUMBER', 'CALC', 'RECORD_NUMBER', 'LOOKUP'], // CALCは除外
  LINK: ["LINK", "LOOKUP"],
  RECORD_NUMBER: ["NUMBER", /*'CALC',*/ "RECORD_NUMBER", "LOOKUP"],
  // テーブル内フィールドは指定不可
};

const allowedConditionFieldTypes = [
  "SINGLE_LINE_TEXT",
  "LOOKUP",
  "NUMBER",
  "LINK",
  "RECORD_NUMBER",
];

const ReferenceTableOptions: React.FC<ReferenceTableProps> = ({
  options,
  onChange,
  getFieldsForApp,
  isReadOnly = false,
  appList = [], // デフォルト値は空配列
}) => {
  // 外部から受け取ったアプリ一覧を使用する
  const [appOptions, setAppOptions] = useState<AppOption[]>([]);

  // 現在のアプリのフィールド
  const [currentAppFields, setCurrentAppFields] = useState<FieldOption[]>([]);

  // 選択されたアプリのフィールド
  const [referenceAppFields, setReferenceAppFields] = useState<FieldOption[]>([]);

  // 参照先アプリのフィールド変更監視フラグ
  const [needsUpdate, setNeedsUpdate] = useState<boolean>(false);

  const [selectedConditionField, setSelectedConditionField] = useState<FieldOption | null>(null);
  const [filteredReferenceFields, setFilteredReferenceFields] = useState<FieldOption[]>([]);

  // 現在の設定値を取得（メモ化して参照の安定化）
  const referenceTable = React.useMemo(
    () =>
      options.referenceTable || {
        relatedApp: { app: "", code: "" },
        condition: { field: "", relatedField: "" },
        displayFields: [],
        sort: "",
        size: "5",
      },
    [options.referenceTable],
  );

  // 選択されているアプリのID
  const selectedAppId = referenceTable.relatedApp.app;

  // 参照先アプリのフィールド変更時に整合性を検証し更新する
  const validateAndUpdateReferences = React.useCallback(
    (fields: FieldOption[]) => {
      // 存在するフィールドIDリスト（参照先アプリ）
      const validFieldIds = fields.map((f) => f.id);

      // 存在するフィールドIDリスト（現在のアプリ）
      const validCurrentFieldIds = currentAppFields.map((f) => f.id);

      // 条件フィールドの検証・更新
      const updatedCondition = { ...referenceTable.condition };

      // フィールドが削除されていないか確認
      if (updatedCondition.field && !validCurrentFieldIds.includes(updatedCondition.field)) {
        updatedCondition.field = "";
        updatedCondition.relatedField = ""; // 左が消えたら右もリセット
        setSelectedConditionField(null); // Stateもリセット
      }

      if (updatedCondition.relatedField && !validFieldIds.includes(updatedCondition.relatedField)) {
        updatedCondition.relatedField = "";
      }

      // 表示フィールドの検証・更新
      const updatedDisplayFields = referenceTable.displayFields.filter((id) =>
        validFieldIds.includes(id),
      );

      if (referenceTable.displayFields.length !== updatedDisplayFields.length) {
        // TODO: エラーメッセージを表示
      }

      // ソート条件の検証・更新
      let updatedSort = "";
      if (referenceTable.sort) {
        const sortConditions = referenceTable.sort.split(",").map((item) => {
          const [field, order] = item.trim().split(" ");
          return { field, order };
        });

        const validSortConditions = sortConditions.filter(
          (sort) => sort.field && validFieldIds.includes(sort.field),
        );

        if (sortConditions.length !== validSortConditions.length) {
          // TODO: エラーメッセージを表示
        }

        updatedSort = validSortConditions.map((sort) => `${sort.field} ${sort.order}`).join(", ");
      }

      // 変更があれば設定を更新
      const hasConditionChanged =
        updatedCondition.field !== referenceTable.condition.field ||
        updatedCondition.relatedField !== referenceTable.condition.relatedField;

      const hasDisplayFieldsChanged =
        updatedDisplayFields.length !== referenceTable.displayFields.length ||
        JSON.stringify(updatedDisplayFields) !== JSON.stringify(referenceTable.displayFields);

      if (hasConditionChanged || hasDisplayFieldsChanged || updatedSort !== referenceTable.sort) {
        onChange("referenceTable", {
          ...referenceTable,
          condition: updatedCondition,
          displayFields: updatedDisplayFields,
          sort: updatedSort,
        });
      }
    },
    [currentAppFields, referenceTable, onChange],
  );

  const handleReferenceFieldChangeDirect = React.useCallback(
    (fieldId: string) => {
      onChange("referenceTable", {
        ...referenceTable,
        condition: {
          ...referenceTable.condition,
          relatedField: fieldId,
        },
      });
    },
    [onChange, referenceTable],
  );

  // アプリリストが更新されたら、アプリオプションを更新
  useEffect(() => {
    if (appList && appList.length > 0) {
      const newAppOptions = appList.map((app) => ({
        id: app.app_id,
        name: app.app_name,
        code: app.app_name.replace(/アプリ$/, ""), // アプリという文字を除去してコード化
      }));
      setAppOptions(newAppOptions);
    }
  }, [appList]);

  // 選択されたアプリが変更されたときに関連フィールドを更新
  useEffect(() => {
    if (selectedAppId) {
      // 実際のアプリのフィールド一覧を取得
      const fields = getFieldsForApp(selectedAppId).map((field) => ({
        id: field.code,
        name: field.label,
        type: field.type,
      }));

      setReferenceAppFields(fields);

      // 参照先アプリのフィールドが変更された場合、設定の整合性チェック
      if (needsUpdate) {
        validateAndUpdateReferences(fields);
        setNeedsUpdate(false);
      }
    } else {
      setReferenceAppFields([]);
    }
  }, [selectedAppId, getFieldsForApp, needsUpdate, validateAndUpdateReferences]);

  // 現在のアプリのフィールドを取得（親コンポーネントから取得）
  const currentAppId = options["currentAppId"] || "";
  useEffect(() => {
    const fields = getFieldsForApp(currentAppId);
    const newFields = fields.map((field) => ({
      id: field.code,
      name: field.label,
      type: field.type,
    }));

    // フィールドの変更を検出
    const newFieldIds = new Set(newFields.map((f) => f.id));

    // 削除されたフィールドがあるか確認
    const hasDeletedFields = currentAppFields.some((field) => !newFieldIds.has(field.id));

    setCurrentAppFields(newFields);

    // 現在のアプリのフィールドが変更され、かつ削除されたフィールドがある場合にのみ検証を実行
    if (selectedAppId && referenceAppFields.length > 0 && hasDeletedFields) {
      validateAndUpdateReferences(referenceAppFields);
    }
  }, [
    getFieldsForApp,
    currentAppId,
    selectedAppId,
    referenceAppFields,
    validateAndUpdateReferences,
    currentAppFields,
  ]);

  useEffect(() => {
    if (selectedConditionField?.type) {
      const allowedTypes = allowedReferenceFieldTypesMap[selectedConditionField.type] || [];
      const filtered = referenceAppFields.filter(
        (field) => field.type && allowedTypes.includes(field.type) && field.type !== "SUBTABLE", // テーブルは除外
      );
      setFilteredReferenceFields(filtered);
    } else {
      setFilteredReferenceFields([]); // 条件フィールドが選択されていない場合は空
    }
  }, [selectedConditionField, referenceAppFields]); // フィルタリングは両方に依存

  useEffect(() => {
    if (selectedConditionField !== null) {
      handleReferenceFieldChangeDirect("");
    }
  }, [selectedConditionField, handleReferenceFieldChangeDirect]); // リセットは selectedConditionField のみに依存

  const handleBooleanOptionChange = (checked: boolean) => {
    onChange("noLabel", checked);
  };

  // アプリ選択が変更された時の処理
  const handleAppChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const appId = e.target.value;
    // 選択されたアプリの情報を取得
    const selectedApp = appOptions.find((app) => app.id === appId);
    const appCode = selectedApp?.code || "";

    // 新しいアプリを設定
    onChange("referenceTable", {
      ...referenceTable,
      relatedApp: {
        app: appId,
        code: appCode,
      },
      condition: { field: "", relatedField: "" },
      displayFields: [],
      sort: "",
      size: referenceTable.size || "5",
    });

    // 参照先アプリが変更されたので、フィールド更新フラグを立てる
    setNeedsUpdate(true);
    setSelectedConditionField(null); // アプリが変わったら条件フィールド選択もリセット
  };

  // 条件フィールドの選択が変更された時の処理
  const handleConditionFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const fieldId = e.target.value;
    const field = currentAppFields.find((f) => f.id === fieldId) || null;
    setSelectedConditionField(field);
    // onChange で state を更新
    onChange("referenceTable", {
      ...referenceTable,
      condition: {
        ...referenceTable.condition,
        field: fieldId,
        relatedField: "", // 左が変わったら右はリセット
      },
    });
  };

  const handleReferenceFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleReferenceFieldChangeDirect(e.target.value);
  };

  // 表示フィールドの選択が変更された時の処理
  const handleDisplayFieldChange = (fieldId: string, checked: boolean) => {
    let newDisplayFields: string[];

    if (checked) {
      // 選択された場合は追加
      newDisplayFields = [...referenceTable.displayFields, fieldId];
    } else {
      // 選択解除された場合は削除
      newDisplayFields = referenceTable.displayFields.filter((id) => id !== fieldId);
    }

    onChange("referenceTable", {
      ...referenceTable,
      displayFields: newDisplayFields,
    });
  };

  // ソートフィールドと順序の選択が変更された時の処理
  const handleSortChange = (fieldId: string, order: string) => {
    // 既存のソート条件を解析
    const sortItems = referenceTable.sort
      ? referenceTable.sort.split(",").map((item) => item.trim())
      : [];

    // 新しいソート条件
    const newSort = `${fieldId} ${order}`;

    // 同じフィールドのソート条件があれば置き換え、なければ追加
    const fieldExists = sortItems.findIndex((item) => item.startsWith(`${fieldId} `));

    if (fieldExists !== -1) {
      sortItems[fieldExists] = newSort;
    } else {
      sortItems.push(newSort);
    }

    onChange("referenceTable", {
      ...referenceTable,
      sort: sortItems.join(", "),
    });
  };

  // 表示件数の変更処理
  const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const size = e.target.value;

    onChange("referenceTable", {
      ...referenceTable,
      size,
    });
  };

  // 現在のソート条件を解析
  const currentSortConditions = referenceTable.sort
    ? referenceTable.sort.split(",").map((item) => {
        const [field, order] = item.trim().split(" ");
        return { field, order };
      })
    : [];

  // 関連付けるアプリの選択部分の修正
  const getAppName = (appId: string): string => {
    const app = appOptions.find((app) => String(app.id) === String(appId));
    return app ? app.name : "Unknown App";
  };

  // 条件設定用にフィルタリング（テーブルフィールド除外）
  const filteredCurrentAppFieldsForCondition = currentAppFields.filter(
    (f) => f.type !== "SUBTABLE",
  );

  return (
    <div className={styles["optionSection"]}>
      {/* チェックボックスオプション */}
      <div className={styles["form-group"]}>
        <CustomCheckbox
          label="フィールド名を表示しない"
          checked={options.noLabel || false}
          onChange={handleBooleanOptionChange}
          disabled={isReadOnly}
        />
      </div>

      {/* 参照するアプリの選択 */}
      <div className={styles["form-group"]}>
        <label>参照するアプリ</label>
        <select
          value={selectedAppId}
          onChange={handleAppChange}
          disabled={isReadOnly}
          className={styles["select-input"]}
        >
          <option value="">アプリを選択してください</option>
          {appOptions.map((app) => (
            <option key={app.id} value={app.id}>
              {app.name}
            </option>
          ))}
        </select>
        {selectedAppId && !appOptions.some((app) => app.id === selectedAppId) && (
          <div style={{ color: "red", marginTop: "5px" }}>
            選択されたアプリID: {selectedAppId} ({getAppName(selectedAppId)})
          </div>
        )}
      </div>

      {selectedAppId && (
        <>
          {/* 表示するレコードの条件設定 */}
          <div className={styles["form-group"]}>
            <label>表示するレコードの条件</label>
            <div className={styles["condition-container"]}>
              <select
                value={referenceTable.condition.field}
                onChange={handleConditionFieldChange}
                disabled={isReadOnly}
                className={styles["select-input"]}
              >
                <option value="">このアプリのフィールドを選択</option>
                {/* --- 選択可能なフィールドタイプで更にフィルタリング --- START */}
                {filteredCurrentAppFieldsForCondition
                  .filter((field) => field.type && allowedConditionFieldTypes.includes(field.type))
                  .map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.name} ({field.type})
                    </option>
                  ))}
                {/* --- 選択可能なフィールドタイプで更にフィルタリング --- END */}
              </select>

              <div className={styles["condition-equals"]}>=</div>

              <select
                value={referenceTable.condition.relatedField}
                onChange={handleReferenceFieldChange}
                disabled={isReadOnly || !selectedConditionField} // 条件フィールドが選択されるまで無効
                className={styles["select-input"]}
              >
                <option value="">参照するアプリのフィールドを選択</option>
                {filteredReferenceFields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.name} ({field.type})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 表示するフィールドの選択 */}
          <div className={styles["form-group"]}>
            <label>表示するフィールド</label>
            <div className={styles["fields-container"]}>
              {referenceAppFields.map((field) => (
                <div key={field.id} className={styles["field-checkbox"]}>
                  <CustomCheckbox
                    label={field.name}
                    checked={referenceTable.displayFields.includes(field.id)}
                    onChange={(checked) => handleDisplayFieldChange(field.id, checked)}
                    disabled={isReadOnly}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* レコードのソート設定 */}
          <div className={styles["form-group"]}>
            <label>レコードのソート</label>
            <div className={styles["sort-container"]}>
              {/* 現在のソート条件の表示 */}
              {currentSortConditions.length > 0 && (
                <div className={styles["current-sort"]}>
                  <p>現在のソート条件:</p>
                  <ul>
                    {currentSortConditions.map((sort, index) => {
                      const field = referenceAppFields.find((f) => f.id === sort.field);

                      // フィールドが見つからない場合は表示しない
                      if (!field) {
                        return null;
                      }

                      const orderText = sort.order === "asc" ? "昇順" : "降順";
                      return (
                        <li key={index}>
                          {field.name} ({orderText})
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={() => {
                                // 特定のソート条件を削除
                                const newSortItems = currentSortConditions.filter(
                                  (_, i) => i !== index,
                                );
                                onChange("referenceTable", {
                                  ...referenceTable,
                                  sort: newSortItems.map((s) => `${s.field} ${s.order}`).join(", "),
                                });
                              }}
                              className={styles["remove-sort"]}
                            >
                              ×
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* 新しいソート条件の追加 */}
              {!isReadOnly && (
                <div className={styles["add-sort"]}>
                  <p>ソート条件を追加:</p>
                  <div className={styles["sort-field-container"]}>
                    <select
                      className={styles["select-input"]}
                      onChange={(e) => {
                        const fieldId = e.target.value;
                        if (fieldId) {
                          handleSortChange(fieldId, "asc"); // デフォルトは昇順
                          e.target.value = ""; // セレクトボックスをリセット
                        }
                      }}
                    >
                      <option value="">ソートするフィールドを選択</option>
                      {referenceAppFields.map((field) => (
                        <option
                          key={field.id}
                          value={field.id}
                          disabled={currentSortConditions.some((s) => s.field === field.id)}
                        >
                          {field.name}
                        </option>
                      ))}
                    </select>

                    <div className={styles["sort-order-buttons"]}>
                      <button
                        type="button"
                        onClick={() => {
                          // 現在のソート条件を変更（最後に追加されたもの）
                          if (currentSortConditions.length > 0) {
                            const lastSort =
                              currentSortConditions[currentSortConditions.length - 1];
                            lastSort?.field && handleSortChange(lastSort.field, "asc");
                          }
                        }}
                        className={`${styles["sort-button"]} ${styles["sort-asc"]}`}
                      >
                        昇順
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // 現在のソート条件を変更（最後に追加されたもの）
                          if (currentSortConditions.length > 0) {
                            const lastSort =
                              currentSortConditions[currentSortConditions.length - 1];
                            lastSort?.field && handleSortChange(lastSort.field, "desc");
                          }
                        }}
                        className={`${styles["sort-button"]} ${styles["sort-desc"]}`}
                      >
                        降順
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 表示件数の設定 */}
          <div className={styles["form-group"]}>
            <label>表示件数</label>
            <select
              value={referenceTable.size || "5"}
              onChange={handleSizeChange}
              disabled={isReadOnly}
              className={styles["select-input"]}
            >
              <option value="5">5件</option>
              <option value="10">10件</option>
              <option value="20">20件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
            </select>
          </div>
        </>
      )}
    </div>
  );
};

export default ReferenceTableOptions;
